import { useMemo, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Building2, Plus, Search } from 'lucide-react';

import {
  createClient,
  listClientTypes,
  listClients,
  LIFECYCLE_STAGES,
  type ClientLifecycleStage,
  type ClientListItem,
} from '@/api/clients.api';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/useToast';
import { hasCapability } from '@/lib/capabilities';

type BadgeVariant = 'neutral' | 'success' | 'warning' | 'danger' | 'info';

const STAGE_VARIANT: Record<ClientLifecycleStage, BadgeVariant> = {
  lead: 'neutral',
  qualified: 'info',
  onboarding: 'warning',
  live: 'success',
  dormant: 'neutral',
  lost: 'danger',
};

const HEALTH_VARIANT: Record<string, BadgeVariant> = {
  on_track: 'success',
  at_risk: 'warning',
  stalled: 'danger',
};

function titleCase(s: string): string {
  return s.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

export function ClientsPage() {
  const { state } = useAuth();
  const me = state.status === 'authenticated' ? state.me : null;
  const canUse = me ? hasCapability(me, 'use_clients') : false;

  const [stage, setStage] = useState<ClientLifecycleStage | 'all'>('all');
  const [search, setSearch] = useState('');
  const [creating, setCreating] = useState(false);

  const filters = useMemo(
    () => ({
      ...(stage !== 'all' ? { lifecycleStage: [stage] } : {}),
      ...(search.trim() ? { q: search.trim() } : {}),
    }),
    [stage, search],
  );

  const clientsQuery = useQuery({
    queryKey: ['clients', filters],
    queryFn: () => listClients(filters),
    enabled: canUse,
  });

  if (!canUse) {
    return (
      <div className="p-6">
        <Card className="p-6 text-kw-muted">You don’t have access to the client CRM.</Card>
      </div>
    );
  }

  const rows = clientsQuery.data?.rows ?? [];

  return (
    <div className="space-y-4 p-4 sm:p-6">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="typo-h1 flex items-center gap-2">
            <Building2 className="size-6 text-kw-cerulean-dark" /> Clients
          </h1>
          <p className="mt-1 text-[13px] text-kw-muted">
            The lead → live pipeline. {clientsQuery.data ? `${String(clientsQuery.data.total)} total.` : ''}
          </p>
        </div>
        <Button
          onClick={() => {
            setCreating(true);
          }}
        >
          <Plus className="size-4" /> New client
        </Button>
      </header>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => {
            setStage('all');
          }}
          className={filterChip(stage === 'all')}
        >
          All
        </button>
        {LIFECYCLE_STAGES.map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => {
              setStage(s);
            }}
            className={filterChip(stage === s)}
          >
            {titleCase(s)}
          </button>
        ))}
        <div className="relative ml-auto">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-kw-muted" />
          <Input
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
            }}
            placeholder="Search clients…"
            className="w-56 pl-8"
          />
        </div>
      </div>

      {/* Table */}
      <Card className="overflow-hidden p-0">
        {clientsQuery.isLoading ? (
          <div className="p-6 text-kw-muted">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="p-6 text-kw-muted">No clients match. Create one to get started.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-[13px]">
              <thead className="border-b border-kw-border text-kw-muted">
                <tr>
                  <th className="px-4 py-2.5 font-medium">Client</th>
                  <th className="px-4 py-2.5 font-medium">Type</th>
                  <th className="px-4 py-2.5 font-medium">Stage</th>
                  <th className="px-4 py-2.5 font-medium">Onboarding</th>
                  <th className="px-4 py-2.5 font-medium">Updated</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((c) => (
                  <ClientRow key={c.id} client={c} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {creating && (
        <NewClientModal
          onClose={() => {
            setCreating(false);
          }}
        />
      )}
    </div>
  );
}

function filterChip(active: boolean): string {
  return [
    'rounded-kw-md px-3 py-1.5 text-[13px] transition-colors',
    active ? 'bg-kw-cerulean-dark text-white' : 'bg-kw-bg text-kw-shark hover:bg-kw-border',
  ].join(' ');
}

function ClientRow({ client }: { client: ClientListItem }) {
  return (
    <tr className="border-b border-kw-border last:border-0 hover:bg-kw-bg">
      <td className="px-4 py-3">
        <Link to={`/clients/${client.id}`} className="font-medium text-kw-cerulean-dark hover:underline">
          {client.name}
        </Link>
        {client.tags.length > 0 && (
          <div className="mt-0.5 flex flex-wrap gap-1">
            {client.tags.slice(0, 3).map((t) => (
              <span key={t} className="text-[11px] text-kw-muted">
                #{t}
              </span>
            ))}
          </div>
        )}
      </td>
      <td className="px-4 py-3 text-kw-muted">{client.clientType.name}</td>
      <td className="px-4 py-3">
        <Badge variant={STAGE_VARIANT[client.lifecycleStage]}>{titleCase(client.lifecycleStage)}</Badge>
      </td>
      <td className="px-4 py-3">
        {client.onboardingStatus ? (
          <Badge variant={HEALTH_VARIANT[client.onboardingStatus] ?? 'info'}>
            {titleCase(client.onboardingStatus)}
          </Badge>
        ) : client.onboardingBrandId ? (
          <span className="text-[12px] text-kw-muted">Linked</span>
        ) : (
          <span className="text-kw-muted">—</span>
        )}
      </td>
      <td className="px-4 py-3 text-kw-muted">{new Date(client.updatedAt).toLocaleDateString()}</td>
    </tr>
  );
}

function NewClientModal({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const { show } = useToast();
  const [name, setName] = useState('');
  const [clientTypeId, setClientTypeId] = useState('');

  const typesQuery = useQuery({ queryKey: ['client-types'], queryFn: listClientTypes });

  const mutation = useMutation({
    mutationFn: () => createClient({ name: name.trim(), clientTypeId }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['clients'] });
      show('Client created.', { variant: 'success' });
      onClose();
    },
    onError: () => {
      show('Could not create the client.', { variant: 'danger' });
    },
  });

  const types = typesQuery.data ?? [];
  const canSubmit = name.trim().length > 0 && clientTypeId.length > 0 && !mutation.isPending;

  function submit(e: FormEvent) {
    e.preventDefault();
    if (canSubmit) mutation.mutate();
  }

  return (
    <Modal open onClose={onClose} title="New client">
      <form onSubmit={submit} className="space-y-4">
        <label className="block">
          <span className="text-[13px] font-medium text-kw-shark">Company name</span>
          <Input
            value={name}
            onChange={(e) => {
              setName(e.target.value);
            }}
            placeholder="Acme Brands"
            autoFocus
          />
        </label>
        <label className="block">
          <span className="text-[13px] font-medium text-kw-shark">Client type</span>
          <select
            value={clientTypeId}
            onChange={(e) => {
              setClientTypeId(e.target.value);
            }}
            className="mt-1 w-full rounded-kw-md border border-kw-border bg-white px-3 py-2 text-[14px]"
          >
            <option value="">Select a type…</option>
            {types.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </select>
          {types.length === 0 && !typesQuery.isLoading && (
            <span className="mt-1 block text-[12px] text-kw-muted">
              No client types configured yet — an admin can add them in settings.
            </span>
          )}
        </label>
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={!canSubmit}>
            Create
          </Button>
        </div>
      </form>
    </Modal>
  );
}
